import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuthStore } from '@/stores/authStore';

// Type-safe wrappers for tables not yet in generated types
const adminClient = supabase as any;

export function useIsAdmin() {
  const user = useAuthStore((s) => s.user);
  return useQuery({
    queryKey: ['isAdmin', user?.id],
    queryFn: async () => {
      if (!user) return false;
      const { data } = await adminClient
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .maybeSingle();
      return !!data;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });
}

// Feature Flags
export function useFeatureFlags() {
  return useQuery({
    queryKey: ['featureFlags'],
    queryFn: async () => {
      const { data, error } = await adminClient
        .from('feature_flags')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as any[];
    },
  });
}

export function useCreateFeatureFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (flag: { name: string; description?: string; status?: string }) => {
      const { data, error } = await adminClient
        .from('feature_flags')
        .insert(flag)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['featureFlags'] }),
  });
}

export function useUpdateFeatureFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string; status?: string; name?: string; description?: string }) => {
      const updateData: Record<string, unknown> = { ...updates, updated_at: new Date().toISOString() };
      if (updates.status === 'stable') {
        updateData.launched_at = new Date().toISOString();
      }
      const { data, error } = await adminClient
        .from('feature_flags')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['featureFlags'] }),
  });
}

export function useDeleteFeatureFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await adminClient.from('feature_flags').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['featureFlags'] }),
  });
}

// App Releases
export function useAppReleases() {
  return useQuery({
    queryKey: ['appReleases'],
    queryFn: async () => {
      const { data, error } = await adminClient
        .from('app_releases')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as any[];
    },
  });
}

export function useCreateRelease() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  return useMutation({
    mutationFn: async (release: { version: string; name?: string; release_notes?: string }) => {
      const { data, error } = await adminClient
        .from('app_releases')
        .insert({ ...release, created_by: user?.id })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['appReleases'] }),
  });
}

export function useUpdateRelease() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string; status?: string; name?: string; release_notes?: string }) => {
      const updateData: Record<string, unknown> = { ...updates };
      if (updates.status === 'stable') {
        updateData.launched_at = new Date().toISOString();
      }
      const { data, error } = await adminClient
        .from('app_releases')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['appReleases'] }),
  });
}

// Beta Users
export function useBetaUsers() {
  return useQuery({
    queryKey: ['betaUsers'],
    queryFn: async () => {
      const { data, error } = await adminClient
        .from('beta_users')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as any[];
    },
  });
}

export function useAllProfiles() {
  return useQuery({
    queryKey: ['allProfiles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, avatar_url');
      if (error) throw error;
      return data;
    },
  });
}

export function useAddBetaUser() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  return useMutation({
    mutationFn: async (userId: string) => {
      const { data, error } = await adminClient
        .from('beta_users')
        .insert({ user_id: userId, added_by: user?.id })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['betaUsers'] }),
  });
}

export function useRemoveBetaUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await adminClient.from('beta_users').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['betaUsers'] }),
  });
}
